// OAuth 2.0 PKCE utilities
import { base64URLEncode, sha256 } from './crypto';

export interface PKCEPair {
  codeVerifier: string;
  codeChallenge: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string; // Optional for backward compatibility with token rotation
  id_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserInfo {
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  name: string;
  program_tag: string;
  tony_ai_access: string;
  client_id: string;
  token_use: string;
  scope: string;
  auth_time: number;
  iat: number;
  exp: number;
}

// PKCE code challenge generation
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const hashed = await sha256(verifier);
  return base64URLEncode(new Uint8Array(hashed));
}

export async function generatePKCEPair(): Promise<PKCEPair> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  return {
    codeVerifier,
    codeChallenge,
  };
}

// Storage utilities
export const PKCE_STORAGE_KEY = 'oauth_code_verifier';
export const REFRESH_TOKEN_KEY = 'oauth_refresh_token';

export function storeCodeVerifier(verifier: string): void {
  localStorage.setItem(PKCE_STORAGE_KEY, verifier);
}

export function getCodeVerifier(): string | null {
  return localStorage.getItem(PKCE_STORAGE_KEY);
}

export function clearCodeVerifier(): void {
  localStorage.removeItem(PKCE_STORAGE_KEY);
}

export function storeRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearTokens(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}
